import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import {
  Plus, Search, Pencil, Trash2, Phone, Mail, MapPin, Key, ShieldOff, ShieldCheck,
  Copy, Eye, EyeOff, Building2, ShoppingCart, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_FORM = {
  company_name: '', contact_name: '', email: '', phone: '', address: '',
  siret: '', sector: '', notes: '',
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);
  const [search, setSearch] = useState('');
  const [credentialsDialog, setCredentialsDialog] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const { data } = await api.get(`/clients${params}`);
      setClients(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSave = async () => {
    try {
      if (editingClient) {
        await api.put(`/clients/${editingClient.id}`, form);
        toast.success('Client mis à jour');
        setDialogOpen(false);
      } else {
        const { data } = await api.post('/clients', form);
        toast.success('Client créé');
        setDialogOpen(false);
        if (data.generated_password) {
          setCredentialsDialog({
            company: data.company_name,
            login: data.login_email,
            password: data.generated_password,
          });
        }
      }
      setEditingClient(null);
      setForm(INITIAL_FORM);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (c) => {
    setEditingClient(c);
    setForm({
      company_name: c.company_name || '',
      contact_name: c.contact_name || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      siret: c.siret || '',
      sector: c.sector || '',
      notes: c.notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Supprimer le client ${name} et son compte utilisateur ?`)) return;
    try { await api.delete(`/clients/${id}`); toast.success('Supprimé'); fetchClients(); }
    catch { toast.error('Erreur'); }
  };

  const handleToggleAccess = async (c) => {
    try {
      const { data } = await api.put(`/clients/${c.id}/toggle-access`);
      toast.success(data.message);
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) { toast.error('Mot de passe trop court'); return; }
    try {
      await api.put(`/clients/${passwordDialog.id}/password`, { new_password: newPassword });
      toast.success('Mot de passe modifié');
      setPasswordDialog(null);
      setNewPassword('');
      fetchClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié');
  };

  const handleNew = () => { setEditingClient(null); setForm(INITIAL_FORM); setDialogOpen(true); };

  return (
    <div className="space-y-6" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Clients</h1>
          <p className="text-sm text-zinc-500 mt-1">{clients.length} client(s)</p>
        </div>
        <Button data-testid="new-client-btn" onClick={handleNew} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouveau client
        </Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input data-testid="client-search" placeholder="Rechercher un client..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-zinc-900 border-zinc-800 text-zinc-100 rounded-sm" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="bg-zinc-900 border border-zinc-800 rounded-sm h-56 nexus-shimmer" />)}
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-16 text-zinc-500">Aucun client enregistré</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((c) => (
            <Card key={c.id} className="bg-zinc-900 border-zinc-800 rounded-sm nexus-card" data-testid={`client-card-${c.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-base font-semibold text-zinc-100 truncate flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-amber-500/70 shrink-0" />
                      {c.company_name}
                    </h3>
                    {c.contact_name && <p className="text-sm text-zinc-400 mt-0.5">{c.contact_name}</p>}
                  </div>
                  {c.login_email ? (
                    <span className={`text-xs px-2 py-0.5 rounded-sm border ${c.account_active ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-rose-500/10 text-rose-400 border-rose-500/20'}`}>
                      {c.account_active ? 'Actif' : 'Accès révoqué'}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-sm border bg-zinc-800 text-zinc-500 border-zinc-700">Sans compte</span>
                  )}
                </div>

                <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                  {c.sector && (
                    <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-sm">{c.sector}</span>
                  )}
                  {c.self_registered && (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded-sm">
                      <UserCheck className="w-2.5 h-2.5" /> Inscription libre
                    </span>
                  )}
                </div>

                {/* Credentials */}
                {c.login_email && (
                  <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 mb-3">
                    <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">Identifiants de connexion</p>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-blue-400 font-mono truncate mr-2">{c.login_email}</span>
                      <button onClick={() => copyToClipboard(c.login_email)} className="text-zinc-600 hover:text-zinc-300 transition-colors shrink-0">
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                    {c.plain_password && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-amber-400/80 font-mono">{c.plain_password}</span>
                        <button onClick={() => copyToClipboard(c.plain_password)} className="text-zinc-600 hover:text-zinc-300 transition-colors shrink-0">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-1.5 text-xs text-zinc-500 mb-3">
                  {c.email && <div className="flex items-center gap-2 truncate"><Mail className="w-3 h-3 shrink-0" />{c.email}</div>}
                  {c.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 shrink-0" />{c.phone}</div>}
                  {c.address && <div className="flex items-center gap-2 truncate"><MapPin className="w-3 h-3 shrink-0" />{c.address}</div>}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-zinc-800 mb-2">
                  <div className="text-xs text-zinc-500 flex items-center gap-1.5">
                    <ShoppingCart className="w-3 h-3" />
                    <span className="text-zinc-300 font-medium">{c.order_count || 0}</span> commande{(c.order_count || 0) > 1 ? 's' : ''}
                  </div>
                  {c.siret && <span className="text-[10px] text-zinc-600 font-mono">SIRET {c.siret}</span>}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 pt-2 border-t border-zinc-800/50">
                  <button data-testid={`edit-client-${c.id}`} onClick={() => handleEdit(c)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors" title="Modifier">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {c.login_email && (
                    <>
                      <button data-testid={`password-client-${c.id}`} onClick={() => { setPasswordDialog(c); setNewPassword(''); }} className="p-1.5 text-zinc-500 hover:text-blue-400 transition-colors" title="Changer mot de passe">
                        <Key className="w-3.5 h-3.5" />
                      </button>
                      <button data-testid={`toggle-client-${c.id}`} onClick={() => handleToggleAccess(c)}
                        className={`p-1.5 transition-colors ${c.account_active ? 'text-zinc-500 hover:text-rose-400' : 'text-zinc-500 hover:text-emerald-400'}`}
                        title={c.account_active ? "Révoquer l'accès" : "Réactiver l'accès"}>
                        {c.account_active ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                      </button>
                    </>
                  )}
                  <button data-testid={`delete-client-${c.id}`} onClick={() => handleDelete(c.id, c.company_name)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors ml-auto" title="Supprimer">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-xl max-h-[85vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingClient ? 'Modifier le client' : 'Nouveau client'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editingClient ? 'Modifiez les informations.' : 'Un identifiant et mot de passe seront générés automatiquement pour son espace client.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Société *</Label>
              <Input data-testid="form-client-company" value={form.company_name} onChange={(e) => setForm(f => ({ ...f, company_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Nom de la société" />
              {!editingClient && form.company_name && (
                <p className="text-[11px] text-blue-400 font-mono">Identifiant prévu : {form.company_name.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 20)}@prodpilot.fr</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Contact</Label>
              <Input value={form.contact_name} onChange={(e) => setForm(f => ({ ...f, contact_name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" placeholder="Prénom Nom" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Email pro</Label>
              <Input value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Secteur</Label>
              <Input value={form.sector} onChange={(e) => setForm(f => ({ ...f, sector: e.target.value }))} placeholder="Aéronautique, Auto..."
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">SIRET</Label>
              <Input value={form.siret} onChange={(e) => setForm(f => ({ ...f, siret: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Adresse</Label>
              <Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="col-span-2 space-y-2">
              <Label className="text-zinc-400 text-sm">Notes internes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-client-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editingClient ? 'Mettre à jour' : 'Créer le client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog open={!!credentialsDialog} onOpenChange={() => setCredentialsDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="credentials-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-emerald-400" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Client créé avec succès
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              Voici les identifiants de connexion. Communiquez-les au client.
            </DialogDescription>
          </DialogHeader>
          {credentialsDialog && (
            <div className="space-y-4 py-4">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4 space-y-3">
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Société</p>
                  <p className="text-sm text-zinc-200 font-medium">{credentialsDialog.company}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Identifiant de connexion</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-blue-400 font-mono break-all">{credentialsDialog.login}</p>
                    <button onClick={() => copyToClipboard(credentialsDialog.login)} className="text-zinc-500 hover:text-zinc-200 ml-2 shrink-0">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Mot de passe</p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-amber-400 font-mono">{credentialsDialog.password}</p>
                    <button onClick={() => copyToClipboard(credentialsDialog.password)} className="text-zinc-500 hover:text-zinc-200">
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-xs text-zinc-500">Vous retrouverez ces identifiants directement sur la fiche du client.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setCredentialsDialog(null)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium w-full">
              Compris, fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-sm" data-testid="password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {passwordDialog?.company_name} — {passwordDialog?.login_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Nouveau mot de passe</Label>
              <div className="relative">
                <Input
                  data-testid="new-password-input"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nouveau mot de passe"
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pr-10"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordDialog(null)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-password-btn" onClick={handleChangePassword} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
